import React, { useState } from "react";
import { Twitter, Linkedin, Facebook, Mail, Link as LinkIcon, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * ShareButtons — Twitter, LinkedIn, Facebook, Email, and a one-click copy.
 * No 3rd-party SDKs (no tracking pixels). Pure intent URLs + clipboard API.
 */
export function ShareButtons({ url, title, layout = "horizontal" }) {
  const [copied, setCopied] = useState(false);
  const u = encodeURIComponent(url || "");
  const t = encodeURIComponent(title || "");
  const targets = [
    { name: "X / Twitter", icon: Twitter, href: `https://twitter.com/intent/tweet?url=${u}&text=${t}` },
    { name: "LinkedIn",    icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${u}` },
    { name: "Facebook",    icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${u}` },
    { name: "Email",       icon: Mail,     href: `mailto:?subject=${t}&body=${u}` },
  ];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy — please copy from the address bar.");
    }
  };

  const wrap =
    layout === "vertical"
      ? "flex flex-col gap-2"
      : "flex flex-wrap items-center gap-2";

  return (
    <div className={wrap} data-testid="blog-share-buttons">
      {targets.map(({ name, icon: Icon, href }) => (
        <a
          key={name}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Share on ${name}`}
          data-testid={`share-${name.toLowerCase().split(" ")[0]}`}
          className="w-9 h-9 rounded-full border border-[#E5E7EB] bg-white text-[#374151] flex items-center justify-center hover:border-[#0A2540] hover:text-[#0A2540] transition-colors"
        >
          <Icon className="w-4 h-4" />
        </a>
      ))}
      <button
        type="button"
        onClick={copy}
        aria-label="Copy link"
        data-testid="share-copy"
        className={
          "w-9 h-9 rounded-full border flex items-center justify-center transition-colors " +
          (copied
            ? "border-[#10B981] bg-[#10B981] text-white"
            : "border-[#E5E7EB] bg-white text-[#374151] hover:border-[#0A2540] hover:text-[#0A2540]")
        }
      >
        {copied ? <Check className="w-4 h-4" /> : <LinkIcon className="w-4 h-4" />}
      </button>
    </div>
  );
}
